import { useState } from 'react';
import { useAuthStore, useUiStore, useSettingsStore } from '@/stores';
import { UsersManager } from './UsersManager';
import { Button } from '@/components/common/Button';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import { useToast } from '@/components/common/Toast';
import { exportStandaloneHtml } from '@/utils/exportHtml';
import {
  Moon, Sun, RotateCcw, LogOut, Download,
  KeyRound, Lock, Loader2
} from 'lucide-react';
import { clsx } from 'clsx';

export function SystemConfig() {
  const { user, token, logout } = useAuthStore();
  const { theme, toggleTheme } = useUiStore();
  const settings = useSettingsStore();
  const { showToast } = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const isAdmin = user?.role === 'admin';

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) {
      showToast('Por favor completa todos los campos de contraseña', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('La confirmación de la nueva contraseña no coincide', 'error');
      return;
    }
    if (newPassword.length < 6) {
      showToast('La nueva contraseña debe tener al menos 6 caracteres', 'error');
      return;
    }

    setIsChangingPassword(true);
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        showToast(data.error || 'Error al cambiar contraseña', 'error');
        return;
      }
      showToast('Contraseña actualizada con éxito', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      showToast(err?.message || 'Error de conexión con el servidor', 'error');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleExportHtml = async () => {
    setIsExporting(true);
    try {
      exportStandaloneHtml();
      showToast('Copia de seguridad descargada con éxito', 'success');
    } catch (err: any) {
      showToast(err?.message || 'No se pudo descargar la copia de seguridad', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const confirmReset = () => {
    settings.resetSettings();
    setShowResetConfirm(false);
    showToast('Ajustes restablecidos a valores por defecto de fábrica', 'info');
  };

  return (
    <div className="space-y-6">
      {/* 1. Mi Cuenta & Seguridad Personal */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-4 shadow-xs">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center shrink-0">
              <Lock size={16} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-text-primary">Mi Cuenta & Contraseña</h3>
              <p className="text-[11px] text-text-muted">
                Usuario activo: <strong className="text-text-primary">{user?.username}</strong> ({user?.name || user?.role})
              </p>
            </div>
          </div>

          <span className={clsx(
            'text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider',
            isAdmin ? 'bg-brand-primary/10 text-brand-primary' : 'bg-surface-secondary text-text-secondary'
          )}>
            {isAdmin ? 'Administrador' : 'Personal de Atención'}
          </span>
        </div>

        <form onSubmit={handleChangePassword} className="space-y-3 pt-2 border-t border-border-light">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            <div>
              <label className="text-[11px] font-medium text-text-primary block mb-1">Contraseña Actual</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="••••••"
                className="w-full px-3 py-2 bg-surface-secondary border border-border rounded-xl text-xs"
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-text-primary block mb-1">Nueva Contraseña</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className="w-full px-3 py-2 bg-surface-secondary border border-border rounded-xl text-xs"
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-text-primary block mb-1">Confirmar Nueva</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la contraseña"
                className="w-full px-3 py-2 bg-surface-secondary border border-border rounded-xl text-xs"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              size="sm"
              disabled={isChangingPassword || !currentPassword || !newPassword}
              className="text-xs"
            >
              {isChangingPassword ? (
                <>
                  <Loader2 size={13} className="animate-spin mr-1" />
                  <span>Actualizando...</span>
                </>
              ) : (
                <>
                  <KeyRound size={13} className="mr-1" />
                  <span>Cambiar Contraseña</span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* 2. Gestión de Personal & Accesos (UsersManager) */}
      <div className="space-y-3">
        <UsersManager />
      </div>

      {/* 3. Preferencias de Visualización y Tema */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-3 shadow-xs">
        <div className="flex items-center justify-between text-xs">
          <div>
            <span className="font-semibold text-text-primary block">Tema Visual de la Aplicación</span>
            <p className="text-[11px] text-text-muted">Alterna entre modo Claro y Oscuro</p>
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            className="flex items-center gap-1.5 px-3 py-2 bg-surface-secondary border border-border rounded-xl text-xs font-semibold text-text-primary cursor-pointer hover:bg-surface-tertiary transition-colors shadow-2xs"
          >
            {theme === 'dark' ? <Moon size={14} className="text-indigo-400" /> : <Sun size={14} className="text-amber-500" />}
            <span>Modo {theme === 'dark' ? 'Oscuro' : 'Claro'}</span>
          </button>
        </div>
      </div>

      {/* 4. Mantenimiento y Zona de Acción */}
      <div className="bg-surface border border-border rounded-xl p-4 space-y-4 shadow-xs">
        <h3 className="text-sm font-semibold text-text-primary">Mantenimiento & Exportación</h3>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-1 border-t border-border-light text-xs">
          <div>
            <span className="font-medium text-text-primary block">Descargar Copia de Respaldo</span>
            <p className="text-[11px] text-text-muted">Descarga un archivo con tus citas y clientes para consultar sin conexión</p>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={isExporting}
            onClick={handleExportHtml}
            className="text-xs shrink-0"
          >
            <Download size={13} className="mr-1" />
            <span>Descargar Respaldo</span>
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2 border-t border-border-light text-xs">
          <div>
            <span className="font-medium text-text-primary block">Restablecer Ajustes de Fábrica</span>
            <p className="text-[11px] text-text-muted">Restaura todas las plantillas y reglas a sus valores por defecto</p>
          </div>
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={() => setShowResetConfirm(true)}
            className="text-xs shrink-0"
          >
            <RotateCcw size={13} className="mr-1" />
            <span>Restablecer Todo</span>
          </Button>
        </div>

        <div className="flex justify-end pt-2 border-t border-border-light">
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={logout}
            className="text-xs"
          >
            <LogOut size={13} className="mr-1" />
            <span>Cerrar Sesión Activa</span>
          </Button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showResetConfirm}
        title="Restablecer Ajustes de Fábrica"
        message="¿Estás seguro de que deseas restablecer todos los ajustes a los valores predeterminados? Esta acción sobrescribirá tus plantillas, reglas y configuraciones personalizadas."
        confirmLabel="Restablecer Ajustes"
        cancelLabel="Cancelar"
        variant="danger"
        onConfirm={confirmReset}
        onCancel={() => setShowResetConfirm(false)}
      />
    </div>
  );
}
